import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Clock, Check, AlertCircle } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";

export const Contact = () => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
    consent: false,
  });
  const [status, setStatus] = useState({ type: null, msg: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: null, msg: "" });
    setSubmitting(true);
    try {
      await api.post("/contact", { ...form, lang });
      setStatus({ type: "success", msg: t.contact.form.success });
      setForm({
        name: "",
        email: "",
        company: "",
        subject: "",
        message: "",
        consent: false,
      });
    } catch (err) {
      // Log full error for debugging in browser console
      console.error("Contact form error:", err);
      console.error("Response:", err.response);

      let errorMsg;
      if (!err.response) {
        // Network error (no response from server)
        errorMsg = `${t.contact.form.network_error} (${err.message})`;
      } else if (err.response.status === 422 && Array.isArray(err.response.data?.detail)) {
        // FastAPI validation error
        const fields = err.response.data.detail
          .map((e) => {
            const field = e.loc?.slice(-1)?.[0] || "field";
            return `${field}: ${e.msg}`;
          })
          .join(" · ");
        errorMsg = `${t.contact.form.validation_error}: ${fields}`;
      } else {
        // Backend returned an error with detail
        errorMsg =
          formatApiErrorDetail(err.response.data?.detail) ||
          `${t.contact.form.error} (HTTP ${err.response.status})`;
      }
      setStatus({ type: "error", msg: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#F8F9FA] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: Info */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="flex items-center gap-4 mb-8"
            >
              <span className="h-px w-10 bg-[#0D2F4C]" />
              <span
                data-testid="contact-overline"
                className="text-xs uppercase tracking-[0.3em] font-bold text-[#0D2F4C]"
              >
                {t.contact.overline}
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              data-testid="contact-title"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-[#0D2F4C] tracking-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t.contact.title}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-[#4B5563] text-base leading-relaxed mb-12"
            >
              {t.contact.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="space-y-6 border-t border-[#0D2F4C]/10 pt-10"
            >
              <div className="flex items-start gap-4">
                <MapPin className="text-[#0D2F4C] mt-0.5" size={18} strokeWidth={1.5} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#0D2F4C]/60 mb-1">
                    {t.contact.info_title}
                  </div>
                  <div className="text-[#0D2F4C]" data-testid="contact-address">
                    {t.contact.address}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="text-[#C9A961] mt-0.5" size={18} strokeWidth={1.5} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-1">
                    {t.contact.dakar_title}
                  </div>
                  <div className="text-[#0D2F4C] leading-relaxed" data-testid="contact-dakar-address">
                    {t.contact.dakar_lines.map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="text-[#0D2F4C] mt-0.5" size={18} strokeWidth={1.5} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#0D2F4C]/60 mb-1">
                    E-mail
                  </div>
                  <a
                    href={`mailto:${t.contact.email}`}
                    className="text-[#0D2F4C] hover:underline"
                    data-testid="contact-email"
                  >
                    {t.contact.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="text-[#0D2F4C] mt-0.5" size={18} strokeWidth={1.5} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#0D2F4C]/60 mb-1">
                    {t.contact.hours_label}
                  </div>
                  <div className="text-[#0D2F4C]" data-testid="contact-hours">
                    {t.contact.hours}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-white p-8 sm:p-12 border border-[#0D2F4C]/10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <FormField
                  label={t.contact.form.name}
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  testid="contact-name-input"
                />
                <FormField
                  label={t.contact.form.email}
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  testid="contact-email-input"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <FormField
                  label={t.contact.form.company}
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  testid="contact-company-input"
                />
                <FormField
                  label={t.contact.form.subject}
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  required
                  testid="contact-subject-input"
                />
              </div>

              <div className="mb-6">
                <label className="block text-xs uppercase tracking-[0.2em] text-[#0D2F4C]/70 mb-2">
                  {t.contact.form.message} <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  required
                  rows={5}
                  data-testid="contact-message-input"
                  className="w-full px-0 py-2 bg-transparent border-b border-[#0D2F4C]/30 text-[#0D2F4C] focus:outline-none focus:border-[#0D2F4C] transition-colors resize-none"
                />
              </div>

              <label className="flex items-start gap-3 mb-8 cursor-pointer">
                <input
                  type="checkbox"
                  name="consent"
                  checked={form.consent}
                  onChange={onChange}
                  required
                  data-testid="contact-consent-checkbox"
                  className="mt-1 w-4 h-4 accent-[#0D2F4C]"
                />
                <span className="text-xs text-[#4B5563] leading-relaxed">
                  {t.contact.form.consent}
                </span>
              </label>

              {status.type === "success" && (
                <div
                  data-testid="contact-success-msg"
                  className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 text-green-800 mb-6 text-sm whitespace-pre-line leading-relaxed"
                >
                  <Check size={16} className="mt-0.5 shrink-0" />
                  <span>{status.msg}</span>
                </div>
              )}
              {status.type === "error" && (
                <div
                  data-testid="contact-error-msg"
                  className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 text-red-800 mb-6 text-sm"
                >
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{status.msg}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !form.consent}
                data-testid="contact-submit-btn"
                className="w-full sm:w-auto bg-[#0D2F4C] text-white px-10 py-4 text-sm tracking-wide hover:bg-[#0A243A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: 0 }}
              >
                {submitting ? t.contact.form.submitting : t.contact.form.submit}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const FormField = ({ label, name, value, onChange, type = "text", required, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.2em] text-[#0D2F4C]/70 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="w-full px-0 py-2 bg-transparent border-b border-[#0D2F4C]/30 text-[#0D2F4C] focus:outline-none focus:border-[#0D2F4C] transition-colors"
    />
  </div>
);
